import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { supabase } from '@/lib/supabase'
import { AuthContext } from '@/contexts/authContextDef'

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Crée le profil utilisateur si inexistant (idempotent).
 *  Lit user_metadata.first_name pour pré-remplir le prénom lors du 1er login. */
async function ensureUserProfile(user: import('@supabase/supabase-js').User) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase.from('user_profile') as any)
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle()

  if (!data) {
    const firstName = (user.user_metadata?.first_name as string | undefined) ?? null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase.from('user_profile') as any).insert({
      user_id: user.id,
      first_name: firstName,
      daily_time: '07:00',
      streak_count: 1,
      onboarding_done: false,
    })
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    // Timeout de sécurité : si Supabase ne répond pas dans les 8s → on débloque
    // 3s était trop court sur réseau mobile lent ou après changement de réseau.
    const timeout = setTimeout(() => {
      if (mounted) {
        if (!import.meta.env.PROD) {
          console.warn('[Auth] getSession() timeout après 8s — déconnexion forcée')
        }
        setLoading(false)
      }
    }, 8000)

    const unblock = () => {
      if (mounted) clearTimeout(timeout)
    }

    // Récupère la session existante au démarrage
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (!mounted) return
        unblock()
        if (error && !import.meta.env.PROD) console.error('[Auth] getSession() error:', error.message)
        setSession(session)
        setUser(session?.user ?? null)
        if (session?.user) ensureUserProfile(session.user)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!mounted) return
        unblock()
        if (!import.meta.env.PROD) console.error('[Auth] getSession() a lancé une exception:', err)
        setLoading(false)
      })

    // Écoute tous les changements d'état d'auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return
        unblock()
        setSession(session)
        setUser(session?.user ?? null)
        // Débloque le spinner AVANT tout await pour éviter le hang si Supabase est lent
        setLoading(false)

        if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          ensureUserProfile(session.user).catch(() => {/* non-bloquant */})
        }
      },
    )

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  // ─── Méthodes d'auth ────────────────────────────────────────────────────────

  // URL de production pour les redirections Supabase Auth
  // Utilise VITE_APP_URL si défini, sinon fallback sur le domaine de prod
  const PROD_URL = import.meta.env.VITE_APP_URL ?? 'https://selah-app.fr'

  const signInWithGoogle = useCallback(async () => {
    const redirectTo = Capacitor.isNativePlatform()
      ? 'selah://auth/callback'
      : `${PROD_URL}/`
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    })
  }, [])

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password })
      return error
    },
    [],
  )

  const signUpWithEmail = useCallback(
    async (email: string, password: string, firstName?: string) => {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: `${PROD_URL}/`,
          // Stocke le prénom dans user_metadata — récupéré par ensureUserProfile au 1er login
          ...(firstName ? { data: { first_name: firstName } } : {}),
        },
      })
      return { error, user: data.user, session: data.session }
    },
    [],
  )

  const signOut = useCallback(async () => {
    // Nettoyer les données locales de l'utilisateur (sécurité multi-appareils / multi-comptes)
    // Remove all selah_ keys and cached data to prevent PII leakage between accounts
    // EXCEPT device-level preferences that must persist across sessions
    const PERSIST_ON_LOGOUT = new Set([
      'selah_notif_enabled',       // notification enabled flag — OS notif stays scheduled
      'selah_last_seen_version',   // changelog version — avoid re-showing after re-login
      'selah_review_last_shown',   // cooldown 90j notation — évite re-prompt après reconnexion
      'selah_donation_last_shown', // cooldown 30j/180j donation — idem
      'selah_has_donated',         // statut donateur — persistant
      'selah_first_open_date',     // date première ouverture — ancienneté du compte
      'selah_intro_seen',          // intro slider vu — ne pas re-afficher après reconnexion
    ])
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && (k.startsWith('selah_') || k.startsWith('draft_')) && !PERSIST_ON_LOGOUT.has(k)) {
        toRemove.push(k)
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k))
    await supabase.auth.signOut()
  }, [])

  const value = useMemo(
    () => ({ user, session, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut }),
    [user, session, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
